import { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout as logoutSession } from '@/api/auth';
import { Outlet, useLocation, useNavigate } from 'react-router';
import Header from '@/components/layout/Header/Header';
import Sidebar from '@/components/layout/Sidebar/Sidebar';
import Footer from '@/components/layout/Footer/Footer';
import PageErrorBoundary from '@/components/common/PageErrorBoundary';
import { logoutRequest, fetchUserProfileRequest } from '@/store/actions/accountActions';
import { resetAuthState } from '@/store/actions/authActions';
import type { AppDispatch, RootState } from '@/store';

const SESSION_TIMEOUT_MS = 4 * 60 * 1000 + 30 * 1000;

const AppShell = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const activityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user, userError, loading } = useSelector((state: RootState) => state.account);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.getElementById('main-content')?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    const resetTimer = () => {
      if (sessionExpired) return;
      if (activityTimer.current) clearTimeout(activityTimer.current);
      activityTimer.current = setTimeout(() => {
        setSessionExpired(true);
        setCountdown(30);
        countdownTimer.current = setInterval(() => {
          setCountdown((value) => {
            if (value <= 1) {
              if (countdownTimer.current) clearInterval(countdownTimer.current);
              handleLogout();
              return 0;
            }
            return value - 1;
          });
        }, 1000);
        logoutTimer.current = setTimeout(handleLogout, 30_000);
      }, SESSION_TIMEOUT_MS);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach((event) => window.addEventListener(event, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      if (activityTimer.current) clearTimeout(activityTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      if (countdownTimer.current) clearInterval(countdownTimer.current);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [sessionExpired]);

  const handleLogout = () => {
    logoutSession();
    dispatch(logoutRequest());
    dispatch(resetAuthState());
    navigate('/welcome', { replace: true });
  };

  const continueSession = () => {
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (countdownTimer.current) clearInterval(countdownTimer.current);
    setCountdown(30);
    setSessionExpired(false);
  };

  return (
    <div className="nova-dashboard-layout">
      <Header onMenuToggle={() => setIsSidebarOpen((prev) => !prev)} isMenuOpen={isSidebarOpen} />

      <Sidebar
        user={user}
        userError={userError}
        loading={loading}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onLogout={handleLogout}
        onRetryUserProfile={() => dispatch(fetchUserProfileRequest())}
      />

      <main className="nova-dashboard-main" role="main" id="main-content">
        <div className="nova-dashboard-content">
          <PageErrorBoundary key={location.pathname} onHome={() => navigate('/dashboard')}>
            <Outlet />
          </PageErrorBoundary>
        </div>
      </main>

      <Footer />

      {sessionExpired && (
        <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="session-expiry-title">
          <div className="modal-backdrop show" />
          <div className="modal-dialog modal-dialog-centered" style={{ position: 'relative' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title h5" id="session-expiry-title">Your session is about to end.</h2>
              </div>
              <div className="modal-body">You have been inactive. You will be signed out in {countdown} seconds.</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={handleLogout}>Log out</button>
                <button type="button" className="btn btn-primary" onClick={continueSession}>Stay signed in</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppShell;
